/**
 * Layout-oriented PowerPoint tools: compose_slide (the deterministic template
 * layout engine), align/distribute helpers, and slide-master layouts.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts. Geometry is computed by the pure ../slide-templates module —
 * the model supplies content, the code supplies coordinates.
 */

import type { PptxTool } from "../tool-types";
import { getGeneratedImage } from "../generated-images";
import { listChatFiles, readChatFileWithInfo } from "../staged-data";
import { recordImageProvenance, type ImageSource } from "../image-provenance";
import { getActiveLocalFolderNotice } from "@usable/office-embed";
import {
  buildShape,
  ensureImageFills,
  fetchImageAsDataUrl,
  fitRect,
  normalizeImageToPng,
  resolveShape,
  type Rect,
} from "../shape-builders";
import {
  CANVAS,
  NAMED_PALETTES,
  TEMPLATE_NAMES,
  computeAlignment,
  computeDistribution,
  computeTemplate,
  resolvePalette,
  type Alignment,
  type SlideContent,
  type TemplateName,
} from "../slide-templates";

/** Guarded live probe (same degrade-to-supported rule as pptx-tools). */
function supports(minVersion: string): boolean {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", minVersion);
  } catch {
    return true;
  }
}

/**
 * Resolve a slide-content image (generated-cache / url / base64) to normalized
 * PNG + natural size. Shared by compose_slide and compose_deck. Null when the
 * content requests no image.
 */
export interface ResolvedSlideImage {
  base64: string;
  natural: { width: number; height: number };
  /** Where the bytes came from — recorded per shape for template saving. */
  provenance: { source: ImageSource; ref?: string };
}

export async function resolveSlideImage(
  rawContent: Record<string, unknown>
): Promise<ResolvedSlideImage | null> {
  const useGenerated = rawContent.useGeneratedImage === true;
  const uploadedRef =
    typeof rawContent.useUploadedImage === "string" && rawContent.useUploadedImage.length > 0
      ? (rawContent.useUploadedImage as string)
      : null;
  const hasImage =
    useGenerated ||
    uploadedRef !== null ||
    typeof rawContent.imageBase64 === "string" ||
    typeof rawContent.imageUrl === "string";
  if (!hasImage) return null;

  let rawInput: string;
  let provenance: { source: ImageSource; ref?: string };
  if (uploadedRef) {
    const resolved = await readChatFileWithInfo(uploadedRef);
    rawInput = resolved.dataUrl;
    provenance = { source: resolved.source, ref: resolved.ref };
  } else if (useGenerated) {
    const cached = getGeneratedImage();
    if (!cached) {
      throw new Error(
        "No generated image is cached for this session. Generate an image first, then compose again with useGeneratedImage: true."
      );
    }
    rawInput = `data:${cached.mimeType};base64,${cached.base64}`;
    provenance = { source: "generated", ref: cached.fileId };
  } else if (typeof rawContent.imageUrl === "string") {
    rawInput = await fetchImageAsDataUrl(rawContent.imageUrl);
    provenance = { source: "url", ref: rawContent.imageUrl as string };
  } else {
    rawInput = String(rawContent.imageBase64);
    provenance = { source: "base64" };
  }

  // Normalize to clean PNG: AI-generated images arrive as WebP or line-wrapped
  // base64, which PowerPoint renders as "The picture can't be displayed".
  const normalized = await normalizeImageToPng(rawInput);
  if (!normalized) {
    throw new Error(
      "The slide image could not be decoded — it may be corrupt or an unsupported format. " +
        "Provide a PNG or JPEG as base64 (or a CORS-accessible URL to one), or compose without an image."
    );
  }
  return {
    base64: normalized.base64,
    natural: { width: normalized.width, height: normalized.height },
    provenance,
  };
}

/** Record provenance for image shapes once their ids are sync'd. */
function recordResolvedImages(
  items: Array<{ scope: number | "master"; shape: PowerPoint.Shape; resolved: ResolvedSlideImage }>
): void {
  for (const { scope, shape, resolved } of items) {
    recordImageProvenance({
      scope,
      shapeId: shape.id,
      source: resolved.provenance.source,
      ref: resolved.provenance.ref,
      base64: resolved.base64,
    });
  }
}

/** The content sub-schema shared by compose_slide and compose_deck. */
const SLIDE_CONTENT_SCHEMA = {
  type: "object",
  description: "Slide content — supply the slots the template uses.",
  properties: {
    eyebrow: { type: "string", description: "Small uppercase label above the title." },
    title: { type: "string", description: "Assertion title (required except for quote)." },
    subtitle: { type: "string", description: "Supporting sentence." },
    footer: { type: "string", description: "Muted footer line (cover)." },
    bullets: {
      type: "array",
      description: "Bullet lines (title-bullets, max 6) or callouts (split-hero, max 2).",
      items: { type: "string" },
    },
    stats: {
      type: "array",
      description: "Metrics for stat-band (2–4) or split-hero (max 3).",
      items: {
        type: "object",
        properties: {
          value: { type: "string", description: 'The figure, e.g. "40%", "7 mo".' },
          label: { type: "string", description: "What the figure means." },
        },
        required: ["value", "label"],
      },
    },
    items: {
      type: "array",
      description: "Columns for three-up (2–4).",
      items: {
        type: "object",
        properties: {
          icon: { type: "string", description: "Emoji or numeral stand-in icon." },
          title: { type: "string" },
          body: { type: "string" },
        },
        required: ["title"],
      },
    },
    quote: { type: "string", description: "Quote text (quote template)." },
    attribution: { type: "string", description: "Quote attribution." },
    imageBase64: { type: "string", description: "Image for the split-hero panel (base64/data-URL)." },
    imageUrl: { type: "string", description: "Image URL for the split-hero panel (CORS required)." },
    useGeneratedImage: {
      type: "boolean",
      description:
        "Use the most recent image generated in this chat for the image panel (the add-in caches it automatically — prefer this for generated images).",
    },
    useUploadedImage: {
      type: "string",
      description:
        "Use a file from this chat (paperclip upload — fileName or alias, see list_uploaded_files), the attached local folder (path), or a Usable workspace (fileId from search-files).",
    },
  },
} as const;

/** Logo geometry: 26pt tall, width by aspect (capped), 24pt margin. */
export function computeLogoRect(
  natural: { width: number; height: number },
  position: string
): Rect {
  const height = 26;
  const width = Math.min(140, height * (natural.width / Math.max(natural.height, 1)));
  const margin = 24;
  const left = position === "bottom-left" ? margin : CANVAS.width - margin - width;
  const top = position === "top-right" ? margin : CANVAS.height - margin - height;
  return { left, top, width, height };
}

/** Map raw tool-arg content into the engine's SlideContent. */
function toSlideContent(rawContent: Record<string, unknown>, hasImage: boolean): SlideContent {
  return {
    eyebrow: rawContent.eyebrow as string | undefined,
    title: rawContent.title as string | undefined,
    subtitle: rawContent.subtitle as string | undefined,
    footer: rawContent.footer as string | undefined,
    bullets: rawContent.bullets as string[] | undefined,
    stats: rawContent.stats as SlideContent["stats"],
    items: rawContent.items as SlideContent["items"],
    quote: rawContent.quote as string | undefined,
    attribution: rawContent.attribution as string | undefined,
    hasImage,
  };
}

export const layoutTools: PptxTool[] = [
  // ─── Compose Slide (template layout engine) ───────────────────────────────

  {
    // Text boxes + shapes + fills are 1.4; an image slot needs 1.8 (checked
    // in-handler so the tool itself stays available on 1.4 hosts).
    minRequirementSet: "1.4",
    schema: {
      name: "compose_slide",
      description:
        "Build ONE complete, professionally laid-out slide from a named template — PREFER this " +
        "over sequences of add_text_box/set_text_format calls: all geometry is computed " +
        "deterministically (no misalignment). For a MULTI-SLIDE presentation use compose_deck " +
        "instead (the whole deck in one call — do NOT chain add_slide). Omit slideIndex to " +
        "append a new slide automatically. Templates: cover (opening/section), split-hero " +
        "(assertion title + stats + image panel), three-up (2–4 labelled columns instead of " +
        "bullets), stat-band (2–4 oversized metrics), title-bullets (classic list), quote. " +
        "Returns slot-named shape ids for surgical follow-up edits. Use mode 'replace' on an " +
        "existing slide to rebuild it in one shot.",
      parameters: {
        type: "object",
        properties: {
          template: {
            type: "string",
            description: `Template name. One of: ${TEMPLATE_NAMES.join(", ")}.`,
          },
          slideIndex: {
            type: "number",
            description: "0-based slide to compose onto. Omit to append a new slide.",
          },
          mode: {
            type: "string",
            description:
              '"replace" (default — clears existing shapes on the target slide first) or "merge" (adds on top).',
          },
          palette: {
            type: "string",
            description: `Named palette: ${Object.keys(NAMED_PALETTES).join(", ")} (default dark). For a custom palette pass paletteCustom.`,
          },
          paletteCustom: {
            type: "object",
            description:
              "Optional custom palette {background, surface, text, muted, accent} as hex colours. Overrides palette.",
            properties: {
              background: { type: "string" },
              surface: { type: "string" },
              text: { type: "string" },
              muted: { type: "string" },
              accent: { type: "string" },
            },
          },
          headingFont: { type: "string", description: 'Heading font family (default "Segoe UI").' },
          bodyFont: { type: "string", description: 'Body font family (default "Segoe UI").' },
          content: SLIDE_CONTENT_SCHEMA,
        },
        required: ["template", "content"],
      },
    },
    handler: async (args) => {
      const template = args.template as TemplateName;
      const rawContent = (args.content ?? {}) as Record<string, unknown>;

      // Resolve the image OUTSIDE PowerPoint.run (network + measurement).
      const useGenerated = rawContent.useGeneratedImage === true;
      const hasImage =
        useGenerated ||
        typeof rawContent.imageBase64 === "string" ||
        typeof rawContent.imageUrl === "string";
      if (hasImage && !supports("1.8")) {
        return {
          error:
            "Slide images require PowerPointApi 1.8, which this host does not support. " +
            "Re-run compose_slide without an image — the template will render a labelled placeholder panel.",
          requiredSet: "1.8",
        };
      }

      const image = await resolveSlideImage(rawContent);
      const imageBase64 = image?.base64 ?? null;
      const imageNatural = image?.natural ?? null;

      const content = toSlideContent(rawContent, hasImage);

      const { plans, warnings } = computeTemplate({
        template,
        content,
        palette:
          (args.paletteCustom as Record<string, string> | undefined) &&
          Object.keys(args.paletteCustom as object).length > 0
            ? (args.paletteCustom as unknown as ReturnType<typeof resolvePalette>)
            : (args.palette as string | undefined),
        fonts: {
          ...(typeof args.headingFont === "string" ? { heading: args.headingFont } : {}),
          ...(typeof args.bodyFont === "string" ? { body: args.bodyFont } : {}),
        },
      });
      const palette = resolvePalette(
        (args.paletteCustom as Record<string, string> | undefined) &&
          Object.keys(args.paletteCustom as object).length > 0
          ? (args.paletteCustom as unknown as ReturnType<typeof resolvePalette>)
          : (args.palette as string | undefined)
      );

      return PowerPoint.run(async (context) => {
        // Resolve target slide: explicit index, or append a new one.
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        let slideIndex: number;
        let appended = false;
        if (typeof args.slideIndex === "number") {
          slideIndex = args.slideIndex as number;
          if (slideIndex < 0 || slideIndex >= slides.items.length) {
            throw new Error(
              `slideIndex ${slideIndex} is out of range (presentation has ${slides.items.length} slides).`
            );
          }
        } else {
          slides.add();
          await context.sync();
          slides.load("items");
          await context.sync();
          slideIndex = slides.items.length - 1;
          appended = true;
        }
        const slide = slides.items[slideIndex];

        // mode replace (default): clear existing shapes so the template owns
        // the slide — the one-shot fix for a bad slide.
        const mode = args.mode === "merge" ? "merge" : "replace";
        const existingShapes = slide.shapes;
        existingShapes.load("items/left,items/top,items/width,items/height");
        await context.sync();

        // 4:3/custom-deck heuristic: warn when existing content exceeds the
        // 960×540 canvas this engine assumes (no slide-size API exists).
        const exceedsCanvas = existingShapes.items.some(
          (s) => s.left + s.width > CANVAS.width + 1 || s.top + s.height > CANVAS.height + 1
        );
        if (exceedsCanvas) {
          warnings.push(
            "Existing shapes extend beyond 960×540pt — this deck may not be 16:9; template geometry assumes 16:9."
          );
        }

        // Freshly added slides carry the default LAYOUT's empty placeholders
        // ("Click to add title") which overlap composed content in the editor —
        // always clear appended slides, whatever the mode.
        if ((mode === "replace" || appended) && existingShapes.items.length > 0) {
          existingShapes.items.forEach((s) => s.delete());
          await context.sync();
        }

        // Background field, then every plan in order.
        slide.background.fill.setSolidFill({ color: palette.background });

        const created: Array<{ slot: string; shape: PowerPoint.Shape }> = [];
        const imageFillShapes: Array<{ shape: PowerPoint.Shape; base64: string }> = [];
        for (const plan of plans) {
          if (plan.kind === "image") {
            const rect = imageNatural ? fitRect(imageNatural, plan.rect, "contain") : plan.rect;
            const imageShape = buildShape(slide, { kind: "image", rect, base64: imageBase64 ?? "" });
            created.push({ slot: plan.slot, shape: imageShape });
            if (imageBase64) imageFillShapes.push({ shape: imageShape, base64: imageBase64 });
          } else {
            created.push({ slot: plan.slot, shape: buildShape(slide, plan) });
          }
        }
        created.forEach(({ shape }) => shape.load("id"));
        await context.sync();

        // Verify picture fills rendered (fill.setImage can silently not stick).
        const imageFills = await ensureImageFills(context, imageFillShapes);
        if (imageFills.failed > 0) {
          warnings.push(
            "The slide image fill did NOT apply even after a retry — the shape exists but renders empty. Re-run or insert_image separately."
          );
        }
        if (image) {
          recordResolvedImages(
            imageFillShapes.map(({ shape }) => ({ scope: slideIndex, shape, resolved: image }))
          );
        }

        const shapes: Record<string, { id: string }> = {};
        created.forEach(({ slot, shape }) => {
          shapes[slot] = { id: shape.id };
        });

        return {
          success: true,
          slideIndex,
          template,
          shapes,
          warnings,
          message:
            "Slide composed. Take a screenshot (get_slide_screenshot) to verify it visually; " +
            "use the returned slot shape ids for surgical follow-up edits.",
        };
      });
    },
  },

  // ─── Compose Deck (whole presentation in ONE call) ────────────────────────

  {
    minRequirementSet: "1.4",
    schema: {
      name: "compose_deck",
      description:
        "Build an ENTIRE presentation in ONE call: an array of slide specs, each rendered " +
        "with the same template engine as compose_slide. STRONGLY PREFERRED for any multi-" +
        "slide request ('create a presentation about…') — never chain add_slide calls; this " +
        "tool creates every slide itself. Set replaceExisting: true to replace the current " +
        "deck entirely. Returns per-slide indices, slot shape ids, and warnings; verify with " +
        "ONE screenshot afterwards.",
      parameters: {
        type: "object",
        properties: {
          slides: {
            type: "array",
            description: "Slide specs in presentation order (1–15).",
            items: {
              type: "object",
              properties: {
                template: {
                  type: "string",
                  description: `Template name. One of: ${TEMPLATE_NAMES.join(", ")}.`,
                },
                palette: {
                  type: "string",
                  description: "Per-slide named palette override (defaults to the deck palette).",
                },
                content: SLIDE_CONTENT_SCHEMA,
              },
              required: ["template", "content"],
            },
          },
          palette: {
            type: "string",
            description: `Deck-wide named palette: ${Object.keys(NAMED_PALETTES).join(", ")} (default dark).`,
          },
          paletteCustom: {
            type: "object",
            description:
              "Optional deck-wide custom palette {background, surface, text, muted, accent} as hex colours. Overrides palette.",
            properties: {
              background: { type: "string" },
              surface: { type: "string" },
              text: { type: "string" },
              muted: { type: "string" },
              accent: { type: "string" },
            },
          },
          headingFont: { type: "string", description: 'Heading font family (default "Segoe UI").' },
          bodyFont: { type: "string", description: 'Body font family (default "Segoe UI").' },
          replaceExisting: {
            type: "boolean",
            description:
              "true = the new slides REPLACE the whole existing deck (old slides deleted after the new ones are built). Default false (append at the end).",
          },
          branding: {
            type: "object",
            description:
              "Deck-wide branding applied to EVERY slide — the add-in's slide-master equivalent " +
              "(Office.js cannot edit real masters). Logo source: exactly one of logoBase64 / " +
              "logoUrl / useGeneratedLogo.",
            properties: {
              logoBase64: { type: "string", description: "Logo image as base64/data-URL." },
              logoUrl: { type: "string", description: "Logo URL (CORS-accessible)." },
              logoUpload: {
                type: "string",
                description:
                  "fileName or alias of a logo the user uploaded to this chat via the paperclip (see list_uploaded_files) — PREFER this when the user attached a logo.",
              },
              useGeneratedLogo: {
                type: "boolean",
                description: "Use the most recent image generated in this chat as the logo.",
              },
              logoPosition: {
                type: "string",
                description: '"bottom-right" (default), "bottom-left", or "top-right".',
              },
              footerText: {
                type: "string",
                description: "Small muted footer line on every slide (e.g. company · date).",
              },
            },
          },
        },
        required: ["slides"],
      },
    },
    handler: async (args) => {
      const specs = args.slides as Array<Record<string, unknown>>;
      if (!Array.isArray(specs) || specs.length === 0) {
        throw new Error("compose_deck needs a non-empty slides array.");
      }
      if (specs.length > 15) {
        throw new Error("compose_deck builds at most 15 slides per call — split larger decks.");
      }

      const deckCustom =
        (args.paletteCustom as Record<string, string> | undefined) &&
        Object.keys(args.paletteCustom as object).length > 0
          ? (args.paletteCustom as unknown as ReturnType<typeof resolvePalette>)
          : undefined;
      const deckPalette = deckCustom ?? (args.palette as string | undefined);
      const fonts = {
        ...(typeof args.headingFont === "string" ? { heading: args.headingFont } : {}),
        ...(typeof args.bodyFont === "string" ? { body: args.bodyFont } : {}),
      };

      // Deck branding (the slide-master equivalent): resolve the logo ONCE.
      const branding = (args.branding ?? {}) as Record<string, unknown>;
      const brandingFooter =
        typeof branding.footerText === "string" && branding.footerText.length > 0
          ? (branding.footerText as string)
          : null;
      let logo: ResolvedSlideImage | null = null;
      if (
        branding.useGeneratedLogo === true ||
        typeof branding.logoBase64 === "string" ||
        typeof branding.logoUrl === "string" ||
        typeof branding.logoUpload === "string"
      ) {
        if (!supports("1.8")) {
          throw new Error("Logos require PowerPointApi 1.8 on this host — compose without branding.logo*.");
        }
        logo = await resolveSlideImage({
          useGeneratedImage: branding.useGeneratedLogo,
          useUploadedImage: branding.logoUpload,
          imageBase64: branding.logoBase64,
          imageUrl: branding.logoUrl,
        });
      }
      const logoRect = logo
        ? computeLogoRect(logo.natural, String(branding.logoPosition ?? "bottom-right"))
        : null;

      // Pre-compute every slide OUTSIDE PowerPoint.run: template plans, palette,
      // and images (network + decode). Errors here name the failing slide and
      // abort BEFORE the deck is touched — no half-built state.
      const prepared: Array<{
        template: TemplateName;
        plans: ReturnType<typeof computeTemplate>["plans"];
        warnings: string[];
        palette: ReturnType<typeof resolvePalette>;
        image: ResolvedSlideImage | null;
      }> = [];
      for (let i = 0; i < specs.length; i++) {
        const spec = specs[i];
        const rawContent = (spec.content ?? {}) as Record<string, unknown>;
        try {
          const image = await resolveSlideImage(rawContent);
          if (image && !supports("1.8")) {
            throw new Error("slide images require PowerPointApi 1.8 on this host — compose without images.");
          }
          const slidePalette = deckCustom ?? (spec.palette as string | undefined) ?? deckPalette;
          const { plans, warnings } = computeTemplate({
            template: spec.template as TemplateName,
            content: toSlideContent(rawContent, image !== null),
            palette: slidePalette,
            fonts,
          });
          prepared.push({
            template: spec.template as TemplateName,
            plans,
            warnings,
            palette: resolvePalette(slidePalette),
            image,
          });
        } catch (err) {
          throw new Error(`Slide ${i + 1} of ${specs.length}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();
        const existingCount = slides.items.length;

        const builtSlides: Array<{
          template: TemplateName;
          created: Array<{ slot: string; shape: PowerPoint.Shape }>;
          warnings: string[];
        }> = [];
        // Every picture fill in the deck — verified (and retried) at the end,
        // because fill.setImage can silently not stick (observed live).
        const imageFillShapes: Array<{ shape: PowerPoint.Shape; base64: string }> = [];
        // Shape → resolved image pairs, recorded (with final indexes) post-build.
        const provenanceItems: Array<{
          deckPosition: number;
          shape: PowerPoint.Shape;
          resolved: ResolvedSlideImage;
        }> = [];

        for (let deckPosition = 0; deckPosition < prepared.length; deckPosition++) {
          const slideSpec = prepared[deckPosition];
          slides.add();
          await context.sync();
          slides.load("items");
          await context.sync();
          const slide = slides.items[slides.items.length - 1];

          // Freshly added slides carry the default LAYOUT's empty placeholders
          // ("Click to add title") which overlap composed content in the
          // editor — clear them before building.
          const placeholderShapes = slide.shapes;
          placeholderShapes.load("items");
          await context.sync();
          if (placeholderShapes.items.length > 0) {
            placeholderShapes.items.forEach((s) => s.delete());
            await context.sync();
          }

          slide.background.fill.setSolidFill({ color: slideSpec.palette.background });

          const created: Array<{ slot: string; shape: PowerPoint.Shape }> = [];
          for (const plan of slideSpec.plans) {
            if (plan.kind === "image") {
              const rect = slideSpec.image
                ? fitRect(slideSpec.image.natural, plan.rect, "contain")
                : plan.rect;
              const imageShape = buildShape(slide, {
                kind: "image",
                rect,
                base64: slideSpec.image?.base64 ?? "",
              });
              created.push({ slot: plan.slot, shape: imageShape });
              if (slideSpec.image) {
                imageFillShapes.push({ shape: imageShape, base64: slideSpec.image.base64 });
                provenanceItems.push({ deckPosition, shape: imageShape, resolved: slideSpec.image });
              }
            } else {
              created.push({ slot: plan.slot, shape: buildShape(slide, plan) });
            }
          }
          // Deck branding on every slide (the slide-master equivalent).
          if (logo && logoRect) {
            const logoShape = buildShape(slide, { kind: "image", rect: logoRect, base64: logo.base64 });
            created.push({ slot: "brand_logo", shape: logoShape });
            imageFillShapes.push({ shape: logoShape, base64: logo.base64 });
            provenanceItems.push({ deckPosition, shape: logoShape, resolved: logo });
          }
          if (brandingFooter) {
            created.push({
              slot: "brand_footer",
              shape: buildShape(slide, {
                kind: "textbox",
                rect: { left: 24, top: CANVAS.height - 34, width: 400, height: 20 },
                text: brandingFooter,
                font: { size: 10, color: slideSpec.palette.muted },
              }),
            });
          }

          created.forEach(({ shape }) => shape.load("id"));
          await context.sync();

          builtSlides.push({ template: slideSpec.template, created, warnings: slideSpec.warnings });
        }

        // Verify every picture fill actually rendered; retry stragglers once.
        const imageFills = await ensureImageFills(context, imageFillShapes);
        recordResolvedImages(
          provenanceItems.map(({ deckPosition, shape, resolved }) => ({
            scope: (args.replaceExisting === true ? 0 : existingCount) + deckPosition,
            shape,
            resolved,
          }))
        );
        const deckWarnings: string[] = [];
        if (imageFills.failed > 0) {
          deckWarnings.push(
            `${imageFills.failed} of ${imageFillShapes.length} image fills did NOT apply even after a retry ` +
              `(shapes exist but render empty). Tell the user which images are affected and offer to re-run.`
          );
        }

        // Replace mode: the new deck is fully built — now drop the old slides.
        const replaceExisting = args.replaceExisting === true;
        if (replaceExisting && existingCount > 0) {
          slides.load("items");
          await context.sync();
          for (let i = existingCount - 1; i >= 0; i--) {
            slides.items[i].delete();
          }
          await context.sync();
        }

        const baseIndex = replaceExisting ? 0 : existingCount;
        return {
          success: true,
          imageFills,
          deckWarnings,
          totalSlides: baseIndex + builtSlides.length,
          slides: builtSlides.map((built, i) => {
            const shapes: Record<string, { id: string }> = {};
            built.created.forEach(({ slot, shape }) => {
              shapes[slot] = { id: shape.id };
            });
            return {
              slideIndex: baseIndex + i,
              template: built.template,
              shapes,
              warnings: built.warnings,
            };
          }),
          message:
            "Deck composed in one call. Verify with a single get_slide_screenshot on a key slide; " +
            "use the returned slot shape ids for surgical follow-up edits.",
        };
      });
    },
  },

  // ─── Apply Deck Branding (existing slides — no rebuild) ──────────────────

  {
    minRequirementSet: "1.8",
    schema: {
      name: "apply_deck_branding",
      description:
        "Stamp branding onto the EXISTING deck without rebuilding anything: a logo (bottom-right " +
        "by default) and/or a footer line on every slide (or a subset). Use this when the deck is " +
        "already built and only the branding is missing — do NOT recompose slides just to add a " +
        "logo. Logo source: exactly one of useUploadedImage (paperclip upload OR attached-folder " +
        "path like 'Brand/logo.png'), useGeneratedImage, logoBase64, or logoUrl. Re-running adds " +
        "duplicate shapes — delete previous brand shapes first if re-branding.",
      parameters: {
        type: "object",
        properties: {
          useUploadedImage: {
            type: "string",
            description: "fileName/ref of a chat upload, a path in the attached local folder, OR a Usable workspace fileId (from search-files).",
          },
          useGeneratedImage: { type: "boolean", description: "Use the most recent generated image." },
          logoBase64: { type: "string", description: "Logo as base64/data-URL." },
          logoUrl: { type: "string", description: "Logo URL (CORS-accessible)." },
          logoPosition: {
            type: "string",
            description: '"bottom-right" (default), "bottom-left", or "top-right".',
          },
          footerText: { type: "string", description: "Optional muted footer line for every slide." },
          slideIndexes: {
            type: "array",
            description: "Optional subset of 0-based slide indexes (default: every slide).",
            items: { type: "number" },
          },
        },
      },
    },
    handler: async (args) => {
      const hasLogoSource =
        typeof args.useUploadedImage === "string" ||
        args.useGeneratedImage === true ||
        typeof args.logoBase64 === "string" ||
        typeof args.logoUrl === "string";
      const footerText = typeof args.footerText === "string" && args.footerText.length > 0
        ? (args.footerText as string)
        : null;
      if (!hasLogoSource && !footerText) {
        throw new Error("Provide a logo source and/or footerText — nothing to apply.");
      }

      const logo = hasLogoSource
        ? await resolveSlideImage({
            useUploadedImage: args.useUploadedImage,
            useGeneratedImage: args.useGeneratedImage,
            imageBase64: args.logoBase64,
            imageUrl: args.logoUrl,
          })
        : null;
      const logoRect = logo
        ? computeLogoRect(logo.natural, String(args.logoPosition ?? "bottom-right"))
        : null;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const targets =
          Array.isArray(args.slideIndexes) && (args.slideIndexes as number[]).length > 0
            ? (args.slideIndexes as number[])
            : slides.items.map((_, i) => i);
        const invalid = targets.filter((i) => i < 0 || i >= slides.items.length);
        if (invalid.length > 0) {
          throw new Error(
            `slideIndexes out of range: ${invalid.join(", ")} (presentation has ${slides.items.length} slides).`
          );
        }

        const imageFillShapes: Array<{ shape: PowerPoint.Shape; base64: string }> = [];
        const created: Array<{ slideIndex: number; slot: string; shape: PowerPoint.Shape }> = [];
        for (const slideIndex of targets) {
          const slide = slides.items[slideIndex];
          if (logo && logoRect) {
            const logoShape = buildShape(slide, { kind: "image", rect: logoRect, base64: logo.base64 });
            created.push({ slideIndex, slot: "brand_logo", shape: logoShape });
            imageFillShapes.push({ shape: logoShape, base64: logo.base64 });
          }
          if (footerText) {
            created.push({
              slideIndex,
              slot: "brand_footer",
              shape: buildShape(slide, {
                kind: "textbox",
                rect: { left: 24, top: CANVAS.height - 34, width: 400, height: 20 },
                text: footerText,
                font: { size: 10, color: "#8A8F98" },
              }),
            });
          }
          await context.sync();
        }
        created.forEach(({ shape }) => shape.load("id"));
        await context.sync();

        const imageFills = await ensureImageFills(context, imageFillShapes);
        if (logo) {
          recordResolvedImages(
            created
              .filter(({ slot }) => slot === "brand_logo")
              .map(({ slideIndex, shape }) => ({ scope: slideIndex, shape, resolved: logo }))
          );
        }

        const perSlide: Record<number, Record<string, { id: string }>> = {};
        created.forEach(({ slideIndex, slot, shape }) => {
          perSlide[slideIndex] = perSlide[slideIndex] ?? {};
          perSlide[slideIndex][slot] = { id: shape.id };
        });

        return {
          success: true,
          brandedSlides: targets.length,
          imageFills,
          shapes: perSlide,
          warnings:
            imageFills.failed > 0
              ? [
                  `${imageFills.failed} logo fills did NOT apply even after a retry — tell the user which slides and offer to re-run.`,
                ]
              : [],
          message: "Branding applied to the existing deck. Verify with one get_slide_screenshot.",
        };
      });
    },
  },

  // ─── List Uploaded Files ──────────────────────────────────────────────────

  {
    schema: {
      name: "list_uploaded_files",
      description:
        "List files the user uploaded to this chat via the paperclip (name, alias, type, size). " +
        "Use the fileName or alias with useUploadedImage / branding.logoUpload to place an " +
        "uploaded image or logo on slides.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const { files, notices } = await listChatFiles();
      // Cross-signpost: the user may have attached a LOCAL FOLDER (drawer
      // button) rather than paperclip files — different tools serve it.
      const folderNotice = getActiveLocalFolderNotice();
      if (folderNotice) notices.push(folderNotice);
      return {
        files,
        notices,
        message:
          files.length === 0
            ? notices.length > 0
              ? "No readable chat uploads — see notices for why / what else is attached."
              : "No files uploaded in this chat yet — ask the user to attach one via the paperclip."
            : "Pass a fileName or ref to useUploadedImage (compose content) or branding.logoUpload (compose_deck).",
      };
    },
  },

  // ─── Align Shapes ─────────────────────────────────────────────────────────

  {
    schema: {
      name: "align_shapes",
      description:
        "Align several shapes on a slide against their shared bounding box: left, centerH, " +
        "right (horizontal) or top, centerV, bottom (vertical). Use to clean up a messy slide.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: { type: "number", description: "0-based index of the slide." },
          shapeIds: {
            type: "array",
            description: "IDs of the shapes to align (from get_slide/get_shapes). At least 2.",
            items: { type: "string" },
          },
          alignment: {
            type: "string",
            description: 'One of "left", "centerH", "right", "top", "centerV", "bottom".',
          },
        },
        required: ["slideIndex", "shapeIds", "alignment"],
      },
    },
    handler: async (args) => {
      const alignment = args.alignment as Alignment;
      if (!["left", "centerH", "right", "top", "centerV", "bottom"].includes(alignment)) {
        throw new Error(`Unknown alignment "${String(args.alignment)}".`);
      }
      const ids = args.shapeIds as string[];
      if (!Array.isArray(ids) || ids.length < 2) {
        throw new Error("align_shapes needs at least 2 shapeIds.");
      }
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);
        const shapes = slide.shapes;
        shapes.load("items/id,items/left,items/top,items/width,items/height");
        await context.sync();

        const targets = ids.map((shapeId) => resolveShape(shapes, { shapeId }));
        const rects: Rect[] = targets.map((s) => ({
          left: s.left, top: s.top, width: s.width, height: s.height,
        }));
        const aligned = computeAlignment(rects, alignment);
        targets.forEach((shape, i) => {
          shape.left = aligned[i].left;
          shape.top = aligned[i].top;
        });
        await context.sync();

        return { success: true, moved: ids.length };
      });
    },
  },

  // ─── Distribute Shapes ────────────────────────────────────────────────────

  {
    schema: {
      name: "distribute_shapes",
      description:
        "Distribute shapes evenly along an axis — equal spacing between the first and last, " +
        "or a fixed gap when spacing is given. Use with align_shapes to fix uneven rows/columns.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: { type: "number", description: "0-based index of the slide." },
          shapeIds: {
            type: "array",
            description: "IDs of the shapes to distribute. At least 3 (or 2 with spacing).",
            items: { type: "string" },
          },
          axis: { type: "string", description: '"horizontal" or "vertical".' },
          spacing: {
            type: "number",
            description: "Optional fixed gap in points; omit to spread evenly between first and last.",
          },
        },
        required: ["slideIndex", "shapeIds", "axis"],
      },
    },
    handler: async (args) => {
      const axis = args.axis as "horizontal" | "vertical";
      if (axis !== "horizontal" && axis !== "vertical") {
        throw new Error(`Unknown axis "${String(args.axis)}" — use horizontal or vertical.`);
      }
      const ids = args.shapeIds as string[];
      if (!Array.isArray(ids) || ids.length < 2) {
        throw new Error("distribute_shapes needs at least 2 shapeIds.");
      }
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);
        const shapes = slide.shapes;
        shapes.load("items/id,items/left,items/top,items/width,items/height");
        await context.sync();

        const targets = ids.map((shapeId) => resolveShape(shapes, { shapeId }));
        const rects: Rect[] = targets.map((s) => ({
          left: s.left, top: s.top, width: s.width, height: s.height,
        }));
        const distributed = computeDistribution(
          rects,
          axis,
          typeof args.spacing === "number" ? (args.spacing as number) : undefined
        );
        targets.forEach((shape, i) => {
          shape.left = distributed[i].left;
          shape.top = distributed[i].top;
        });
        await context.sync();

        return { success: true, moved: ids.length };
      });
    },
  },
  // ─── List Layouts ─────────────────────────────────────────────────────────

  {
    // SlideLayout metadata access is PowerPointApi 1.3.
    minRequirementSet: "1.3",
    schema: {
      name: "list_layouts",
      description:
        "List the slide layouts available on the first slide master, with their indices and " +
        "names. Call this before set_layout so you pick a real layout instead of guessing.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const slideMasters = context.presentation.slideMasters;
        slideMasters.load("items/name");
        await context.sync();

        if (slideMasters.items.length === 0) {
          throw new Error("No slide masters found in this presentation.");
        }

        const master = slideMasters.items[0];
        const layouts = master.layouts;
        layouts.load("items/name,items/id");
        await context.sync();

        return {
          master: master.name,
          layouts: layouts.items.map((l, i) => ({ index: i, name: l.name })),
        };
      });
    },
  },

  // ─── Set Layout ───────────────────────────────────────────────────────────

  {
    schema: {
      name: "set_layout",
      description:
        "Apply a slide layout to a slide, chosen by layoutName (preferred — see list_layouts) " +
        "or layoutIndex on the first slide master.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
          layoutName: {
            type: "string",
            description: "Name of the layout (from list_layouts), e.g. 'Blank', 'Title Slide'.",
          },
          layoutIndex: {
            type: "number",
            description: "0-based index of the layout. Used only when layoutName is not given.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const slideMasters = context.presentation.slideMasters;
        slideMasters.load("items");
        await context.sync();

        if (slideMasters.items.length === 0) {
          throw new Error("No slide masters found in this presentation.");
        }

        const masterLayouts = slideMasters.items[0].layouts;
        masterLayouts.load("items/name");
        await context.sync();

        let layout: PowerPoint.SlideLayout;
        if (typeof args.layoutName === "string" && args.layoutName.length > 0) {
          const wanted = (args.layoutName as string).toLowerCase();
          const match = masterLayouts.items.find((l) => l.name.toLowerCase() === wanted);
          if (!match) {
            throw new Error(
              `No layout named "${args.layoutName}". Available: ${masterLayouts.items
                .map((l) => l.name)
                .join(", ")}.`
            );
          }
          layout = match;
        } else if (typeof args.layoutIndex === "number") {
          const layoutIdx = args.layoutIndex as number;
          if (layoutIdx < 0 || layoutIdx >= masterLayouts.items.length) {
            throw new Error(
              `layoutIndex ${layoutIdx} is out of range (0–${masterLayouts.items.length - 1}). Available layouts: ${masterLayouts.items.length}`
            );
          }
          layout = masterLayouts.items[layoutIdx];
        } else {
          throw new Error("Provide layoutName (preferred, see list_layouts) or layoutIndex.");
        }

        slide.applyLayout(layout);

        await context.sync();

        return { success: true };
      });
    },
  },
];
